import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Upload,
  message
} from 'antd';
import { DownloadOutlined, FileExcelOutlined, InboxOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import DspCostImportService from '../../services/dspCostImportService';
import { financialAdjustmentPermissions } from '../../utils/permissions';

const { Dragger } = Upload;
const formatMoney = value => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD'
});

const downloadTemplate = () => {
  try {
    const worksheet = XLSX.utils.aoa_to_sheet([
      ['DSP COST IMPORT TEMPLATE'],
      ['Nhập Start date và End date theo định dạng DD/MM/YYYY. Total = Spend + Phí vận hành + Phí sàn.'],
      [],
      ['Start date', 'End date', 'Spend', 'Phí vận hành', 'Phí sàn', 'Total', 'Currency']
    ]);

    for (let rowNumber = 5; rowNumber <= 24; rowNumber += 1) {
      XLSX.utils.sheet_add_aoa(worksheet, [[null, null, null, null, null, null, 'USD']], {
        origin: `A${rowNumber}`
      });
      worksheet[`F${rowNumber}`] = {
        t: 'n',
        f: `IF(COUNTA(C${rowNumber}:E${rowNumber})=0,"",SUM(C${rowNumber}:E${rowNumber}))`,
        z: '#,##0.00'
      };
    }

    worksheet['!ref'] = 'A1:G24';
    worksheet['!cols'] = [
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 16 },
      { wch: 17 },
      { wch: 12 }
    ];
    worksheet['!merges'] = [
      XLSX.utils.decode_range('A1:G1'),
      XLSX.utils.decode_range('A2:G2')
    ];
    worksheet['!autofilter'] = { ref: 'A4:G24' };

    const instructions = XLSX.utils.aoa_to_sheet([
      ['HƯỚNG DẪN IMPORT DSP COST'],
      ['1', 'Mỗi dòng là một kỳ DSP cost của một Seller Account.'],
      ['2', 'Start date và End date bắt buộc theo định dạng DD/MM/YYYY.'],
      ['3', 'Spend, Phí vận hành và Phí sàn nhập bằng số, không cần ký hiệu $.'],
      ['4', 'Không sửa công thức cột Total: Spend + Phí vận hành + Phí sàn.'],
      ['5', 'Chọn Seller Account trên màn hình trước khi bấm Import.'],
      ['6', 'Các khoảng ngày trong cùng file không được chồng lấn nhau.']
    ]);
    instructions['!cols'] = [{ wch: 7 }, { wch: 85 }];
    instructions['!merges'] = [XLSX.utils.decode_range('A1:B1')];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DSP Cost Import');
    XLSX.utils.book_append_sheet(workbook, instructions, 'Hướng dẫn');
    XLSX.writeFile(workbook, 'dsp_cost_import_template.xlsx');
    message.success('Đã tải template DSP Cost');
  } catch (error) {
    message.error('Không thể tạo template DSP Cost');
  }
};

const DspCostImports = () => {
  const user = useSelector(state => state.auth.user);
  const sellerAccounts = useSelector(state => state.dataCommon.sellerAccounts || []);
  const canImport = financialAdjustmentPermissions.canCreate(user)
    || financialAdjustmentPermissions.canManage(user);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [filterSellerId, setFilterSellerId] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [sellerAccountId, setSellerAccountId] = useState();
  const [fileList, setFileList] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const accountOptions = useMemo(() => sellerAccounts.map(account => ({
    value: account.id,
    label: account.name || account.sellerId
  })), [sellerAccounts]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await DspCostImportService.list({ sellerAccountId: filterSellerId });
      setHistory(response.data?.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || 'Không thể tải lịch sử import DSP cost');
    } finally {
      setLoading(false);
    }
  }, [filterSellerId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const resetModal = () => {
    setSellerAccountId(undefined);
    setFileList([]);
    setPreview(null);
    setPreviewing(false);
  };

  const closeModal = () => {
    if (importing) return;
    setModalOpen(false);
    resetModal();
  };

  const previewFile = async file => {
    setFileList([file]);
    setPreview(null);
    setPreviewing(true);
    try {
      const response = await DspCostImportService.preview(file);
      setPreview(response.data?.data || null);
    } catch (error) {
      message.error(error.response?.data?.message || 'Không thể đọc file DSP cost');
      setFileList([]);
    } finally {
      setPreviewing(false);
    }
    return false;
  };

  const performImport = async replace => {
    setImporting(true);
    try {
      await DspCostImportService.importCosts({
        sellerAccountId,
        sourceFilename: preview.sourceFilename,
        replace,
        rows: preview.rows.map(row => ({
          sourceRowNumber: row.sourceRowNumber,
          startDate: row.startDate,
          endDate: row.endDate,
          spend: row.spend,
          operatingFee: row.operatingFee,
          platformFee: row.platformFee,
          total: row.total,
          currencyCode: row.currencyCode
        }))
      });
      message.success(replace ? 'Đã thay thế DSP cost của kỳ đã chọn' : 'Đã import DSP cost');
      setModalOpen(false);
      resetModal();
      await loadHistory();
    } catch (error) {
      const details = error.response?.data?.details;
      if (error.response?.status === 409 && details?.canReplace && !replace) {
        Modal.confirm({
          title: 'Kỳ DSP cost đã tồn tại',
          content: `Dữ liệu ${details.startDate} - ${details.endDate} đã được import. Thay thế toàn bộ kỳ này bằng file mới?`,
          okText: 'Thay thế',
          cancelText: 'Hủy',
          okButtonProps: { danger: true },
          onOk: () => performImport(true)
        });
      } else {
        message.error(error.response?.data?.message || 'Không thể import DSP cost');
      }
    } finally {
      setImporting(false);
    }
  };

  const previewColumns = [
    { title: 'Dòng', dataIndex: 'sourceRowNumber', width: 65 },
    { title: 'Khoảng ngày', render: (_, row) => row.startDate && row.endDate ? `${row.startDate} → ${row.endDate}` : 'Không hợp lệ' },
    { title: 'Spend', dataIndex: 'spend', align: 'right', render: formatMoney },
    { title: 'Phí vận hành', dataIndex: 'operatingFee', align: 'right', render: formatMoney },
    { title: 'Phí sàn', dataIndex: 'platformFee', align: 'right', render: formatMoney },
    { title: 'Total', dataIndex: 'total', align: 'right', render: value => <strong>{formatMoney(value)}</strong> },
    {
      title: 'Kiểm tra',
      dataIndex: 'errors',
      render: errors => errors?.length
        ? errors.map(error => <Tag key={error} color="error">{error}</Tag>)
        : <Tag color="success">Hợp lệ</Tag>
    }
  ];

  const historyColumns = [
    { title: 'Seller Account', dataIndex: 'sellerAccountName', width: 180 },
    { title: 'Khoảng ngày', render: (_, row) => `${dayjs(row.startDate).format('DD/MM/YYYY')} - ${dayjs(row.endDate).format('DD/MM/YYYY')}`, width: 220 },
    { title: 'Spend', dataIndex: 'spend', align: 'right', render: formatMoney },
    { title: 'Phí vận hành', dataIndex: 'operatingFee', align: 'right', render: formatMoney },
    { title: 'Phí sàn', dataIndex: 'platformFee', align: 'right', render: formatMoney },
    { title: 'DSP Ads Cost', dataIndex: 'total', align: 'right', render: value => <strong>{formatMoney(value)}</strong> },
    { title: 'File', dataIndex: 'sourceFilename', ellipsis: true, width: 200 },
    { title: 'Người import', dataIndex: 'importedBy', render: value => value || '—', width: 180 },
    { title: 'Thời gian import', dataIndex: 'importedAt', render: value => value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—', width: 170 }
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Tất cả Seller Account"
              style={{ width: 240 }}
              options={accountOptions}
              value={filterSellerId}
              onChange={setFilterSellerId}
            />
            <Button icon={<ReloadOutlined />} onClick={loadHistory}>Tải lại</Button>
          </Space>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              Tải template
            </Button>
            {canImport && (
              <Button type="primary" icon={<UploadOutlined />} onClick={() => setModalOpen(true)}>
                Import DSP Cost
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="DSP Ads Cost = Spend + Phí vận hành + Phí sàn"
        description="Chọn Seller Account trước khi import. Tổng của mỗi kỳ được phân bổ đều theo từng ngày để Profit Report lọc đúng theo khoảng thời gian."
      />

      <Table
        rowKey="importBatchId"
        loading={loading}
        columns={historyColumns}
        dataSource={history}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 1350 }}
      />

      <Modal
        title="Import DSP Cost"
        open={modalOpen}
        onCancel={closeModal}
        width={1000}
        okText="Import"
        cancelText="Hủy"
        confirmLoading={importing}
        okButtonProps={{ disabled: !sellerAccountId || !preview?.valid || previewing }}
        onOk={() => performImport(false)}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Chọn Seller Account nhận DSP cost"
            style={{ width: '100%' }}
            options={accountOptions}
            value={sellerAccountId}
            onChange={setSellerAccountId}
          />

          <Dragger
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            fileList={fileList}
            beforeUpload={previewFile}
            onRemove={() => { setFileList([]); setPreview(null); }}
            disabled={previewing || importing}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Kéo file vào đây hoặc bấm để chọn file</p>
            <p className="ant-upload-hint">Hỗ trợ XLSX, XLS và CSV; tối đa 5 MB</p>
          </Dragger>

          {previewing && <Alert type="info" showIcon message="Đang kiểm tra file..." />}
          {preview && (
            <>
              <Descriptions size="small" bordered column={3}>
                <Descriptions.Item label="File"><FileExcelOutlined /> {preview.sourceFilename}</Descriptions.Item>
                <Descriptions.Item label="Số kỳ">{preview.summary?.periodCount || 0}</Descriptions.Item>
                <Descriptions.Item label="Tổng DSP cost">{formatMoney(preview.summary?.total)}</Descriptions.Item>
              </Descriptions>
              {!preview.valid && (
                <Alert type="error" showIcon message="File chưa hợp lệ" description="Sửa các dòng báo lỗi bên dưới rồi tải lại file." />
              )}
              <Table
                rowKey="sourceRowNumber"
                size="small"
                pagination={false}
                columns={previewColumns}
                dataSource={preview.rows || []}
                scroll={{ x: 900 }}
              />
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default DspCostImports;
